import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Phone, Menu, X, Youtube } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '@/components/LanguageSelector';
import NoticeBanner from '@/components/NoticeBanner';
import NewsletterForm from '@/components/NewsletterForm';
import BeforeAfterSlider from '@/components/BeforeAfterSlider';
import ExitPopup from '@/components/ExitPopup';
import Seo from '@/components/Seo';
import { optimizeImage } from '@/utils/imageOptimize';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Home({ user, setUser, cartCount, setCartCount }) {
  const { t } = useTranslation();
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryPairs, setGalleryPairs] = useState([]);
  const [currentPairIndex, setCurrentPairIndex] = useState(0);
  const [pairOrder, setPairOrder] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    updateCartCount();
    // Load default products on mount
    fetchProductsByCategory('mantenimiento');
    setSelectedCategory('mantenimiento');
    // Load gallery images
    fetchGalleryImages();
  }, []);

  useEffect(() => {
    // Rotate pairs randomly every 6 seconds
    if (pairOrder.length > 1) {
      const interval = setInterval(() => {
        setCurrentPairIndex((prev) => (prev + 1) % pairOrder.length);
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [pairOrder]);

  const fetchGalleryImages = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/gallery/hero`);
      const data = await response.json();
      setGalleryImages(data.images || []);
      const pairs = data.pairs || [];
      setGalleryPairs(pairs);
      if (pairs.length > 0) {
        // Shuffle pairs for random display
        const indices = pairs.map((_, i) => i);
        for (let i = indices.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [indices[i], indices[j]] = [indices[j], indices[i]];
        }
        setPairOrder(indices);
      }
    } catch (error) {
      console.error('Error loading gallery:', error);
    }
  };

  useEffect(() => {
    if (selectedCategory) {
      fetchProductsByCategory(selectedCategory);
    }
  }, [selectedCategory, selectedSubcategory]);

  const fetchProductsByCategory = async (category = selectedCategory) => {
    try {
      let url = `${BACKEND_URL}/api/products?category=${category || 'mantenimiento'}`;
      if (selectedSubcategory && selectedSubcategory !== 'all') {
        url += `&subcategory=${selectedSubcategory}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      setFeaturedProducts(data.slice(0, 9));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setSelectedSubcategory('');
    setFeaturedProducts([]);
  };

  const updateCartCount = () => {
    const sessionId = localStorage.getItem('sessionId') || Date.now().toString();
    localStorage.setItem('sessionId', sessionId);
    fetch(`${BACKEND_URL}/api/cart/${sessionId}`)
      .then(res => res.json())
      .then(data => {
        const totalQuantity = data.items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
        setCartCount(totalQuantity);
      })
      .catch(err => console.error(err));
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData);

    try {
      const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
      const response = await fetch(`${BACKEND_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        const result = await response.json();
        localStorage.setItem('token', result.token);
        setUser(result.user);
        setShowAuth(false);
        toast.success(isLogin ? t('auth.session_started') : t('auth.register_success'));
      } else {
        // Surface the backend detail so the customer understands what went
        // wrong (most common case: trying to register with an email that
        // already has an account — we show a one-click recovery shortcut).
        let detail = '';
        try {
          const errBody = await response.json();
          detail = (errBody && errBody.detail) ? errBody.detail : '';
        } catch (e) { /* ignore JSON parse errors */ }

        const isExistingEmail = !isLogin && (
          response.status === 400 &&
          (detail.toLowerCase().includes('ya registrado') || detail.toLowerCase().includes('already'))
        );

        if (isExistingEmail) {
          toast.error(
            'Este email ya tiene una cuenta en ARTHUR FITZ. Inicia sesión o recupera tu contraseña.',
            {
              duration: 10000,
              action: {
                label: 'Recuperar contraseña',
                onClick: () => {
                  // Prefill email and open the forgot-password panel.
                  const email = data.email || '';
                  setForgotMode(true);
                  setTimeout(() => {
                    const input = document.querySelector('input[name="email"]');
                    if (input && email) input.value = email;
                  }, 50);
                },
              },
            }
          );
        } else if (detail) {
          toast.error(detail);
        } else {
          toast.error(t('auth.auth_error'));
        }
      }
    } catch (error) {
      toast.error(t('auth.connection_error'));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    toast.success('Sesión cerrada');
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    const email = new FormData(e.target).get('email');
    if (!email) return;
    setForgotSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();
      toast.success(data.message || 'Si el email existe, recibirás un enlace.');
      setForgotMode(false);
    } catch (err) {
      toast.error(t('auth.connection_error'));
    } finally {
      setForgotSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/34649471889?text=Hola', '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Prótesis capilares y productos de mantenimiento profesional"
        description="Arthur Fitz - Especialistas en sistemas de integración capilar. Prótesis a medida, adhesivos, disolventes y productos de mantenimiento con envío a toda España."
        path="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Store',
          name: 'Arthur Fitz',
          url: 'https://arthurfitz.com',
          telephone: '+34649471889',
          email: 'info@arthurfitz.com',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Gran Vía 46, 9A',
            addressLocality: 'Madrid',
            postalCode: '28013',
            addressCountry: 'ES',
          },
        }}
      />
      <ExitPopup />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Arthur Fitz",
        "url": "https://arthurfitz.com",
        "logo": "https://arthurfitz.com/assets/brand/arthur-fitz-logo.png",
        "description": "Sistemas de Integración Capilar. Prótesis capilares y productos de mantenimiento profesionales.",
        "address": { "@type": "PostalAddress", "streetAddress": "Gran Vía 46, 9A", "addressLocality": "Madrid", "postalCode": "28013", "addressCountry": "ES" },
        "contactPoint": { "@type": "ContactPoint", "telephone": "+34649471889", "contactType": "customer service", "availableLanguage": ["Spanish", "English"] },
        "sameAs": ["https://www.youtube.com/nomascalvos", "https://wa.me/34649471889"]
      }) }} />
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Playfair Display' }} data-testid="logo">
              ARTHUR FITZ
            </Link>

            <nav className="hidden md:flex items-center gap-8">
              <Link to="/productos" className="text-sm font-medium text-gray-700 hover:text-black transition-colors" data-testid="productos-link">
                {t('nav.products')}
              </Link>
              <Link to="/catalogo-protesis" className="text-sm font-medium text-gray-700 hover:text-black transition-colors" data-testid="catalogo-protesis-link">
                CATÁLOGO
              </Link>
              <Link to="/contacto-personalizado" className="text-sm font-medium text-gray-700 hover:text-black transition-colors" data-testid="citas-link">
                {t('nav.contact')}
              </Link>
              <a href="https://www.youtube.com/nomascalvos" target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-black transition-colors" data-testid="youtube-link">
                <Youtube className="w-5 h-5" />
              </a>
              <button onClick={handleWhatsApp} className="text-gray-700 hover:text-black transition-colors" data-testid="whatsapp-btn">
                <Phone className="w-5 h-5" />
              </button>
              <LanguageSelector />
              <Link to="/carrito" className="relative text-gray-700 hover:text-black transition-colors" data-testid="cart-link">
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-black text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-count">
                    {cartCount}
                  </span>
                )}
              </Link>
              {user ? (
                <div className="relative group">
                  <button className="flex items-center gap-2 text-gray-700 hover:text-black transition-colors" data-testid="user-menu">
                    <User className="w-5 h-5" />
                  </button>
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                    <Link to="/perfil" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-testid="perfil-link">
                      {t('nav.my_profile')}
                    </Link>
                    <button onClick={handleLogout} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-testid="logout-btn">
                      {t('nav.logout')}
                    </button>
                  </div>
                </div>
              ) : (
                <Button onClick={() => setShowAuth(true)} className="bg-black text-white hover:bg-gray-800 px-6 py-2 text-sm font-medium" data-testid="login-btn">
                  {t('nav.login')}
                </Button>
              )}
            </nav>

            <button className="md:hidden" onClick={() => setMobileMenu(!mobileMenu)} data-testid="mobile-menu-btn">
              {mobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {mobileMenu && (
            <nav className="md:hidden mt-4 pb-4 flex flex-col gap-4 border-t border-gray-200 pt-4">
              <Link to="/productos" className="text-sm font-medium" data-testid="mobile-productos-link">{t('nav.products')}</Link>
              <Link to="/catalogo-protesis" className="text-sm font-medium" data-testid="mobile-catalogo-protesis-link">CATÁLOGO PRÓTESIS</Link>
              <Link to="/contacto-personalizado" className="text-sm font-medium" data-testid="mobile-citas-link">{t('nav.contact')}</Link>
              <a href="https://www.youtube.com/nomascalvos" target="_blank" rel="noopener noreferrer" className="text-sm font-medium">YOUTUBE</a>
              {user ? (
                <>
                  <Link to="/perfil" className="text-sm font-medium" data-testid="mobile-perfil-link">{t('nav.my_profile')}</Link>
                  <button onClick={handleLogout} className="text-left text-sm font-medium" data-testid="mobile-logout-btn">{t('nav.logout')}</button>
                </>
              ) : (
                <Button onClick={() => setShowAuth(true)} className="bg-black text-white" data-testid="mobile-login-btn">{t('nav.login')}</Button>
              )}
            </nav>
          )}
        </div>
      </header>

      {/* Notice Banner */}
      <NoticeBanner />

      {/* Hero - Main title and CTA */}
      <section className="py-12 sm:py-16 px-6" style={{ backgroundColor: '#e8e6e0' }}>
        <div className="container mx-auto max-w-6xl text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-4 sm:mb-6" style={{ fontFamily: 'Playfair Display' }}>
            {t('home.hero_title')}
          </h1>
          <p className="text-base sm:text-xl text-gray-700 leading-relaxed mb-6 sm:mb-8 max-w-3xl mx-auto font-medium">
            {t('home.hero_subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4 justify-center">
            <Button 
              onClick={() => navigate('/productos')}
              className="bg-black text-white hover:bg-gray-800 px-8 sm:px-10 py-5 sm:py-6 text-base sm:text-lg font-medium w-full sm:w-auto"
            >
              {t('home.view_products')}
            </Button>
            <Button 
              onClick={() => navigate('/contacto-personalizado')}
              className="border-2 border-black text-black bg-white hover:bg-black hover:text-white px-8 sm:px-10 py-5 sm:py-6 text-base sm:text-lg font-medium w-full sm:w-auto"
            >
              {t('home.contact')}
            </Button>
          </div>
        </div>
      </section>

      {/* Catálogo Prótesis Capilares — entrada destacada */}
      <section className="w-full bg-white border-y-2 border-black" data-testid="catalogo-protesis-entry">
        <div className="container mx-auto max-w-6xl px-6 py-14 grid md:grid-cols-2 gap-10 items-center">
          <div className="relative aspect-[4/3] overflow-hidden border-2 border-black bg-gray-100 order-2 md:order-1">
            <img
              src="/assets/catalogo-protesis/inyectado.premium.parting.webp"
              alt="Primer plano de la raya natural del cabello en una prótesis capilar inyectada Arthur Fitz."
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <span className="absolute top-3 left-3 bg-black text-white text-[10px] font-bold tracking-[0.2em] uppercase px-2 py-1">
              Nueva guía
            </span>
          </div>
          <div className="order-1 md:order-2">
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-gray-600">
              Guía comparativa
            </span>
            <h2
              className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-black leading-[1.05]"
              style={{ fontFamily: 'Playfair Display' }}
              data-testid="catalogo-protesis-entry-title"
            >
              Catálogo Prótesis Capilares
            </h2>
            <p className="mt-4 text-base text-gray-700 leading-relaxed">
              Nueve sistemas comparados en una misma vista: V-Loop ultrafina y fina, malla francesa, malla suiza,
              skin anudada, prótesis inyectada y tres construcciones mixtas. Base, densidad, duración orientativa
              y prioridades de cada modelo para que elijas con criterio.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button
                onClick={() => navigate('/catalogo-protesis')}
                className="bg-black text-white hover:bg-gray-800 px-8 py-6 text-base font-medium"
                data-testid="catalogo-protesis-entry-cta"
              >
                Ver catálogo prótesis capilares
              </Button>
              <Button
                onClick={() => navigate('/productos?category=protesis')}
                className="border-2 border-black text-black bg-white hover:bg-black hover:text-white px-8 py-6 text-base font-medium"
              >
                Comprar prótesis
              </Button>
            </div>
          </div>
        </div>
      </section>


      {/* Full Width Images Section - Interactive Before/After Slider */}
      <section className="w-full" data-testid="hero-gallery-section">
        {galleryPairs.length > 0 && pairOrder.length > 0 ? (
          <div className="relative">
            {pairOrder.map((pairIdx, orderPos) => {
              const pair = galleryPairs[pairIdx];
              if (!pair) return null;
              return (
                <div
                  key={pair.pair_id}
                  className={`transition-opacity duration-1000 ${
                    orderPos === currentPairIndex ? 'relative opacity-100' : 'absolute inset-0 opacity-0 pointer-events-none'
                  }`}
                >
                  <BeforeAfterSlider
                    beforeUrl={pair.before?.url}
                    afterUrl={pair.after?.url}
                    title={pair.title}
                  />
                </div>
              );
            })}
            
            {/* Pair indicators */}
            {pairOrder.length > 1 && (
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2 z-10">
                {pairOrder.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentPairIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentPairIndex ? 'bg-white w-8 shadow-lg' : 'bg-white bg-opacity-50'
                    }`}
                    data-testid={`pair-indicator-${index}`}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2">
            <div 
              className="relative h-[600px] md:h-[800px] overflow-hidden group cursor-pointer"
              onClick={() => navigate('/productos?category=protesis')}
            >
              <img 
                src="https://static.wixstatic.com/media/a654e2_5c65284440994890be2f974c33204692~mv2.png"
                alt="Prótesis Capilares"
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-all duration-500"
              />
              <div className="absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-10 transition-all duration-500 flex flex-col items-center justify-end pb-16 text-center px-6">
                <p className="text-white text-2xl font-bold border-2 border-white px-8 py-4 hover:bg-white hover:text-black transition-all shadow-lg">
                  Prótesis Capilares
                </p>
              </div>
            </div>

            <div 
              className="relative h-[600px] md:h-[800px] overflow-hidden group cursor-pointer"
              onClick={() => navigate('/productos?category=mantenimiento')}
            >
              <img 
                src="https://static.wixstatic.com/media/a654e2_9e51c1ca243b41a7aae8ebc374e7bff1~mv2.png"
                alt="Productos de Mantenimiento"
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-all duration-500"
              />
              <div className="absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-10 transition-all duration-500 flex flex-col items-center justify-end pb-16 text-center px-6">
                <p className="text-white text-2xl font-bold border-2 border-white px-8 py-4 hover:bg-white hover:text-black transition-all shadow-lg">
                  Productos de Mantenimiento
                </p>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Featured Products */}
      <section className="py-20 px-6" style={{ backgroundColor: '#e8e6e0' }}>
        <div className="container mx-auto max-w-6xl">
          <div className="mb-12 text-center">
            <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }} data-testid="featured-title">
              {t('home.explore_products')}
            </h2>
            <p className="text-gray-600">{t('home.select_category')}</p>
          </div>

          {/* Category Selectors */}
          <div className="max-w-2xl mx-auto mb-12">
            <div className="bg-white p-6 border-2 border-black">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-2 block font-medium">{t('home.category')}</Label>
                  <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                    <SelectTrigger className="w-full border-2 border-gray-300">
                      <SelectValue placeholder={t('home.select_category_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="protesis">{t('home.prostheses')}</SelectItem>
                      <SelectItem value="mantenimiento">{t('home.maintenance')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {selectedCategory === 'mantenimiento' && (
                  <div>
                    <Label className="mb-2 block font-medium">{t('home.subcategory')}</Label>
                    <Select value={selectedSubcategory} onValueChange={setSelectedSubcategory}>
                      <SelectTrigger className="w-full border-2 border-gray-300">
                        <SelectValue placeholder={t('home.all_subcategories')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('home.all_subcategories')}</SelectItem>
                        <SelectItem value="disolventes">{t('home.solvents')}</SelectItem>
                        <SelectItem value="adhesivos_liquidos">{t('home.liquid_adhesives')}</SelectItem>
                        <SelectItem value="adhesivos_doble_cara">{t('home.tape_adhesives')}</SelectItem>
                        <SelectItem value="otros_mantenimiento">{t('home.other_maintenance')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Products Grid - Simple and elegant */}
          {featuredProducts.length > 0 && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {featuredProducts.map((product) => (
                  <div 
                    key={product.id}
                    className="group cursor-pointer bg-white border border-gray-300 hover:border-black transition-all"
                    onClick={() => navigate(`/producto/${product.id}`)}
                  >
                    <div className="relative overflow-hidden bg-gray-100 aspect-square">
                      <img 
                        src={optimizeImage(product.images[0], 'catalog')}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="font-semibold text-sm mb-1">{product.name}</h3>
                      <span className="text-lg font-bold">{product.price.toFixed(2)}€</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-center mt-10">
                <Button 
                  onClick={() => navigate('/productos')}
                  className="border-2 border-black text-black bg-white hover:bg-black hover:text-white px-10 py-4 text-base font-medium"
                >
                  VER TODO EL CATÁLOGO
                </Button>
              </div>
            </>
          )}

          {featuredProducts.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <p>{t('home.loading_products')}</p>
            </div>
          )}
        </div>
      </section>

      {/* ArtFitz Club Loyalty Program Section */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-gray-500 mb-2">ArtFitz Club</p>
            <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>Programa de Fidelización</h2>
            <p className="text-lg text-gray-600">Cuanto más compres, más te recompensamos</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
            {/* Bronce */}
            <div className="bg-white border-2 border-amber-700 p-5 rounded-xl shadow-md hover:shadow-xl transition-all">
              <div className="mb-4">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-2" style={{ background: 'linear-gradient(135deg, #8B4513 0%, #CD853F 100%)' }}>
                  <span className="text-white font-bold text-lg">B</span>
                </div>
                <h3 className="text-xl font-bold text-amber-900" style={{ fontFamily: 'Playfair Display' }}>Bronce</h3>
              </div>
              <p className="text-sm font-semibold text-gray-700 mb-3">Todos los clientes registrados</p>
              <div className="border-t border-amber-200 pt-3">
                <p className="text-2xl font-bold text-amber-800">3%</p>
                <p className="text-xs text-gray-600">cashback directo</p>
              </div>
            </div>

            {/* Plata */}
            <div className="bg-white border-2 border-gray-400 p-5 rounded-xl shadow-md hover:shadow-xl transition-all">
              <div className="mb-4">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-2" style={{ background: 'linear-gradient(135deg, #B0B0B0 0%, #E8E8E8 100%)' }}>
                  <span className="text-gray-900 font-bold text-lg">P</span>
                </div>
                <h3 className="text-xl font-bold text-gray-700" style={{ fontFamily: 'Playfair Display' }}>Plata</h3>
              </div>
              <p className="text-sm font-semibold text-gray-700 mb-3">desde 500€/año</p>
              <div className="border-t border-gray-200 pt-3">
                <p className="text-2xl font-bold text-gray-700">5%</p>
                <p className="text-xs text-gray-600">descuento</p>
              </div>
            </div>

            {/* Oro */}
            <div className="bg-white border-2 border-yellow-500 p-5 rounded-xl shadow-md hover:shadow-xl transition-all">
              <div className="mb-4">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-2" style={{ background: 'linear-gradient(135deg, #B8860B 0%, #FFD700 100%)' }}>
                  <span className="text-gray-900 font-bold text-lg">O</span>
                </div>
                <h3 className="text-xl font-bold text-yellow-700" style={{ fontFamily: 'Playfair Display' }}>Oro</h3>
              </div>
              <p className="text-sm font-semibold text-gray-700 mb-3">desde 1.000€/año</p>
              <div className="border-t border-yellow-200 pt-3">
                <p className="text-2xl font-bold text-yellow-700">10%</p>
                <p className="text-xs text-gray-600">descuento</p>
              </div>
            </div>

            {/* Platino */}
            <div className="bg-white border-2 p-5 rounded-xl shadow-md hover:shadow-xl transition-all" style={{ borderColor: '#2c3e50' }}>
              <div className="mb-4">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-2" style={{ background: 'linear-gradient(135deg, #546e7a 0%, #37474f 100%)' }}>
                  <span className="text-white font-bold text-lg">P</span>
                </div>
                <h3 className="text-xl font-bold" style={{ fontFamily: 'Playfair Display', color: '#2c3e50' }}>Platino</h3>
              </div>
              <p className="text-sm font-semibold text-gray-700 mb-3">desde 2.000€/año</p>
              <div className="pt-3" style={{ borderTop: '1px solid #cbd5e0' }}>
                <p className="text-2xl font-bold" style={{ color: '#2c3e50' }}>20%</p>
                <p className="text-xs text-gray-600">descuento</p>
              </div>
            </div>

            {/* Pro */}
            <div className="p-5 rounded-xl shadow-2xl hover:shadow-2xl transition-all transform md:scale-105" style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #1A1A1A 100%)', color: '#D4C5B0' }}>
              <div className="mb-4">
                <div className="w-14 h-14 rounded-full flex items-center justify-center mb-2" style={{ background: 'linear-gradient(135deg, #D4C5B0 0%, #fff 100%)' }}>
                  <span className="text-black font-bold text-lg">★</span>
                </div>
                <h3 className="text-xl font-bold" style={{ fontFamily: 'Playfair Display', color: '#D4C5B0' }}>Pro</h3>
              </div>
              <p className="text-sm font-semibold mb-3" style={{ color: '#D4C5B0' }}>desde 3.500€/año</p>
              <div className="pt-3" style={{ borderTop: '1px solid rgba(212,197,176,0.3)' }}>
                <p className="text-2xl font-bold" style={{ color: '#D4C5B0' }}>30%</p>
                <p className="text-xs" style={{ color: 'rgba(212,197,176,0.7)' }}>descuento</p>
              </div>
            </div>
          </div>

          <div className="mt-10 grid md:grid-cols-2 gap-4">
            <div className="bg-gray-50 p-5 border border-gray-300 rounded text-sm text-gray-700">
              <p className="font-bold mb-2">¿Cómo funcionan los Fitz Points?</p>
              <p>1€ gastado en productos Arthur Fitz = 1 punto. Acumulas puntos del 1 de abril al 31 de marzo del año siguiente. No incluyen prótesis personalizadas.</p>
            </div>
            <div className="bg-gray-50 p-5 border border-gray-300 rounded text-sm text-gray-700">
              <p className="font-bold mb-2">Reglas importantes</p>
              <p>Descuentos NO acumulables con cashback ni cupones — elige el que prefieras al pagar. Oro, Platino y Pro conservan su nivel durante el año en curso + año siguiente.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-20 px-6 border-t border-gray-200" style={{ backgroundColor: '#e8e6e0' }}>
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-3 gap-12 text-center">
            <div>
              <h3 className="font-bold mb-2">{t('home.shipping_title')}</h3>
              <p className="text-sm text-gray-600">{t('home.shipping_desc')}</p>
            </div>
            <div>
              <h3 className="font-bold mb-2">{t('home.quality_title')}</h3>
              <p className="text-sm text-gray-600">{t('home.quality_desc')}</p>
            </div>
            <div>
              <h3 className="font-bold mb-2">{t('home.support_title')}</h3>
              <p className="text-sm text-gray-600">{t('home.support_desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img
                src="/assets/brand/arthur-fitz-logo.png"
                alt="Arthur Fitz — Prótesis Capilares"
                width="96"
                height="96"
                loading="lazy"
                className="w-24 h-24 mb-4 bg-white rounded-full p-1"
                data-testid="footer-brand-logo"
              />
              <h3 className="text-xl font-bold mb-2 text-white" style={{ fontFamily: 'Playfair Display' }}>ARTHUR FITZ</h3>
              <p className="text-sm text-gray-400">Prótesis capilares premium</p>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-sm">{t('home.footer_nav')}</h4>
              <div className="space-y-2 text-sm">
                <Link to="/productos" className="block text-gray-400 hover:text-white transition-colors">{t('common.products')}</Link>
                <Link to="/contacto-personalizado" className="block text-gray-400 hover:text-white transition-colors">{t('common.contact')}</Link>
                <a href="https://www.youtube.com/nomascalvos" target="_blank" rel="noopener noreferrer" className="block text-gray-400 hover:text-white transition-colors">YouTube</a>
              </div>
              <h4 className="font-bold mt-6 mb-4 text-sm">Legal</h4>
              <div className="space-y-2 text-sm">
                <Link to="/condiciones" className="block text-gray-400 hover:text-white transition-colors" data-testid="footer-terms-link">Condiciones de venta</Link>
                <Link to="/privacidad" className="block text-gray-400 hover:text-white transition-colors" data-testid="footer-privacy-link">Privacidad</Link>
                <Link to="/cookies" className="block text-gray-400 hover:text-white transition-colors" data-testid="footer-cookies-link">Cookies</Link>
                <Link to="/envios" className="block text-gray-400 hover:text-white transition-colors" data-testid="footer-shipping-link">Envíos y devoluciones</Link>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-sm">{t('home.footer_contact')}</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <p>Gran Vía 46, 9A</p>
                <p>28013 Madrid</p>
                <p>info@arthurfitz.com</p>
                <p>+34 649 471 889</p>
              </div>
            </div>
            <div>
              <NewsletterForm />
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2025 ARTHUR FITZ. {t('home.footer_rights')}</p>
          </div>
        </div>
      </footer>

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={(o) => { setShowAuth(o); if (!o) setForgotMode(false); }}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="auth-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }} data-testid="auth-dialog-title">
              {forgotMode
                ? 'Recuperar contraseña'
                : (isLogin ? t('auth.login_title') : t('auth.register_title'))}
            </DialogTitle>
          </DialogHeader>

          {forgotMode ? (
            <form onSubmit={handleForgotPassword} className="space-y-4" data-testid="forgot-form">
              <p className="text-sm text-gray-600">
                Introduce tu email y te enviaremos un enlace para restablecer tu contraseña.
              </p>
              <div>
                <Label htmlFor="forgot-email">{t('auth.email')}</Label>
                <Input
                  id="forgot-email"
                  name="email"
                  type="email"
                  required
                  autoFocus
                  className="border-gray-300"
                  data-testid="forgot-email-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-black text-white hover:bg-gray-800"
                disabled={forgotSubmitting}
                data-testid="forgot-submit-btn"
              >
                {forgotSubmitting ? 'Enviando...' : 'Enviar enlace'}
              </Button>
              <button
                type="button"
                onClick={() => setForgotMode(false)}
                className="w-full text-center text-sm text-gray-600 hover:text-black"
              >
                Volver al inicio de sesión
              </button>
            </form>
          ) : (
          <form onSubmit={handleAuth} className="space-y-4" data-testid="auth-form">
            {!isLogin && (
              <div>
                <Label htmlFor="name">{t('auth.name')}</Label>
                <Input id="name" name="name" required data-testid="auth-name-input" className="border-gray-300" />
              </div>
            )}
            <div>
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input id="email" name="email" type="email" required data-testid="auth-email-input" className="border-gray-300" />
            </div>
            <div>
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input id="password" name="password" type="password" required data-testid="auth-password-input" className="border-gray-300" />
            </div>
            {!isLogin && (
              <div>
                <Label htmlFor="phone">{t('auth.phone')}</Label>
                <Input id="phone" name="phone" data-testid="auth-phone-input" className="border-gray-300" />
              </div>
            )}
            <Button type="submit" className="w-full bg-black text-white hover:bg-gray-800" data-testid="auth-submit-btn">
              {isLogin ? t('auth.submit_login') : t('auth.submit_register')}
            </Button>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-gray-500">{t('auth.or_continue')}</span>
              </div>
            </div>

            <Button
              type="button"
              onClick={() => {
                const redirectUrl = `${window.location.origin}/`;
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full border-2 border-gray-300 bg-white text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('auth.google')}
            </Button>

            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="w-full text-center text-sm text-gray-600 hover:text-black"
              data-testid="auth-toggle-btn"
            >
              {isLogin ? t('auth.no_account') : t('auth.has_account')}
            </button>

            {isLogin && (
              <button
                type="button"
                onClick={() => setForgotMode(true)}
                className="w-full text-center text-xs text-gray-500 hover:text-black underline"
                data-testid="forgot-password-link"
              >
                ¿Olvidaste tu contraseña?
              </button>
            )}
          </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
